import { Link, useLocation } from 'react-router-dom'

import { mobileNavSections } from '@/config/marketing-nav.config'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

function isFeatureLinkActive(pathname: string, href: string): boolean {
  return href === '/' ? pathname === '/' : pathname.startsWith(href)
}

function isLinkActive(pathname: string, href: string): boolean {
  return href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)
}

type MarketingMobileNavProps = {
  loginHref: string
  signupHref: string
  onNavigate: () => void
}

export function MarketingMobileNav({
  loginHref,
  signupHref,
  onNavigate,
}: MarketingMobileNavProps) {
  const location = useLocation()
  const [featuresSection, ...topLevelSections] = mobileNavSections

  return (
    <nav id="marketing-mobile-nav" className="nav-mobile-sheet__body" aria-label="Mobile">
      <div className="nav-mobile-accordion flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* Features — sub-items shown by default, not collapsible */}
        <div className="flex">
          <p className="nav-mobile-accordion__trigger nav-mobile-accordion__heading">
            {featuresSection.label}
          </p>
        </div>
        <div className="nav-mobile-accordion__content">
          <div className="nav-mobile-accordion__panel">
            <div className="nav-menu-list">
              {featuresSection.links.map((link) => {
                const active = isFeatureLinkActive(location.pathname, link.href)

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'nav-dropdown-link',
                      active && 'nav-dropdown-link--active'
                    )}
                  >
                    {link.label}
                  </Link>
                )
              })}
            </div>
          </div>
        </div>

        {/* Other items — navigate directly, no expand */}
        {topLevelSections.map((section) => {
          const link = section.links[0]
          const active = isLinkActive(location.pathname, link.href)

          return (
            <div key={section.id} className="flex">
              <Link
                to={link.href}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'nav-mobile-accordion__trigger',
                  active && 'nav-mobile-accordion__trigger--active'
                )}
              >
                {section.label}
              </Link>
            </div>
          )
        })}
      </div>

      <div className="nav-mobile-ctas shrink-0 pt-2">
        <a href={loginHref} className="nav-login w-full justify-center text-center">
          Log In
        </a>
        <Button
          asChild
          variant="cta"
          size="cta"
          className="w-full justify-center text-center"
        >
          <a href={signupHref} className="nav-cta">
            Get Started
          </a>
        </Button>
      </div>
    </nav>
  )
}
